import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagement from '../../../../utils/api/location-management';

interface PowerConsumptionReportChartProperties {
  startDate?: string;
  endDate?: string;
}

interface ChartDataProperties {
  date: string;
  hour?: number | string;
  time?: string;
  label: string;
  totalPowerConsumed: number;
  day?: number | string;
  month?: number | string;
  monthName?: string;
  year?: number | string;
  week?: number | string;
  weekName?: string;
  [key: string]: any;
}

interface ChartWidth {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Month translation mapping dictionary.
 */
const monthMap: Record<string, string> = {
  Jan: 'jan',
  Feb: 'feb',
  Mar: 'mar',
  Apr: 'apr',
  May: 'may_month',
  Jun: 'jun',
  Jul: 'jul',
  Aug: 'aug',
  Sep: 'sep',
  Oct: 'oct',
  Nov: 'nov',
  Dec: 'dec',
};

/**
 * Short month names array for month resolution.
 */
const shortMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Formats a chart label based on whether it represents an hour, day/date, week, or month.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {any} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @param {string} [startDate] - Optional start date of current range.
 * @param {string} [endDate] - Optional end date of current range.
 * @returns {string} Formatted label string for the chart axis.
 */
const formatChartLabel = (
  item: ChartDataProperties,
  t: any,
  locale: string = 'en',
  startDate?: string,
  endDate?: string,
): string => {
  // 1. Hour / Time format handling (e.g. { hour: 10, time: "10:00", date: "2026-08-31" })
  if (item.time !== undefined && item.time !== null && item.time !== '') {
    return String(item.time).trim().slice(0, 5);
  }
  if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    if (!Number.isNaN(hourNumber)) {
      return `${String(hourNumber).padStart(2, '0')}:00`;
    }
    return String(item.hour).trim();
  }

  // 2. Specific Date / Day handling (takes priority over standalone month)
  let parsedDate: Date | null = null;
  const rawDate = item.date;

  if (rawDate !== undefined && rawDate !== null && rawDate !== '') {
    const dateString = String(rawDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [yearString, monthPart, dayString] = dateString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [dayString, monthPart, yearString] = dateString.split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (dateString.includes('-') || dateString.includes('/')) {
      const generalDate = new Date(dateString);
      if (!Number.isNaN(generalDate.getTime())) {
        parsedDate = generalDate;
      }
    }
  }

  // If parsed date was not found via rawDate, check day + month numbers
  if (!parsedDate && item.day !== undefined && item.day !== null && item.day !== '') {
    const dayNumber = Number(item.day);
    const rawMonthValue = item.month ?? item.monthName;
    const monthNumber =
      rawMonthValue !== undefined && rawMonthValue !== null && rawMonthValue !== ''
        ? Number(rawMonthValue)
        : NaN;

    if (!Number.isNaN(dayNumber) && dayNumber >= 1 && dayNumber <= 31) {
      let resolvedMonth =
        !Number.isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12 ? monthNumber : NaN;

      if (Number.isNaN(resolvedMonth) && typeof rawMonthValue === 'string') {
        const monthIndex = shortMonths.findIndex(
          (m) => m.toLowerCase() === rawMonthValue.trim().slice(0, 3).toLowerCase(),
        );
        if (monthIndex !== -1) {
          resolvedMonth = monthIndex + 1;
        }
      }

      if (!Number.isNaN(resolvedMonth)) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, resolvedMonth - 1, dayNumber);
        if (!Number.isNaN(dateObject.getTime())) {
          parsedDate = dateObject;
        }
      }
    }
  }

  if (parsedDate) {
    const isMultiYear =
      Boolean(startDate) &&
      Boolean(endDate) &&
      new Date(startDate!).getFullYear() !== new Date(endDate!).getFullYear();

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      ...(isMultiYear ? { year: '2-digit' } : {}),
    }).format(parsedDate);
  }

  // 3. Week format handling
  const weekValue = item.weekName || item.week;
  if (weekValue !== undefined && weekValue !== null && weekValue !== '') {
    const weekString = String(weekValue).trim();
    const weekMatch = weekString.match(/^week\s*(\d+)$/i) || weekString.match(/^w\s*(\d+)$/i);
    if (weekMatch) {
      return `${t('week', { defaultValue: 'Week' })} ${weekMatch[1]}`;
    }
    if (/^\d+$/.test(weekString)) {
      return `${t('week', { defaultValue: 'Week' })} ${weekString}`;
    }
    return weekString;
  }

  // 4. Month format handling (monthly aggregate without day)
  const rawMonth = item.monthName || item.month;
  if (rawMonth !== undefined && rawMonth !== null && rawMonth !== '') {
    const monthString = String(rawMonth).trim();

    // Check numeric month 1-12 or "01"-"12"
    if (/^\d{1,2}$/.test(monthString)) {
      const monthNumber = Number(monthString);
      if (monthNumber >= 1 && monthNumber <= 12) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, monthNumber - 1, 1);
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return item.year ? `${monthShort} ${item.year}` : monthShort;
      }
    }

    // Check "Jan 2024" or "Jan 24" format
    const parts = monthString.split(' ');
    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    // Check "Jan", "Feb" format
    if (monthMap[monthString]) {
      return t(monthMap[monthString]);
    }

    // Check "YYYY-MM" format e.g. "2026-08"
    if (/^\d{4}-\d{2}$/.test(monthString)) {
      const [year, month] = monthString.split('-').map(Number);
      const dateObject = new Date(year, month - 1, 1);
      if (!Number.isNaN(dateObject.getTime())) {
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return `${monthShort} ${year}`;
      }
    }

    return monthString;
  }

  // 5. Fallback on label
  if (item.label !== undefined && item.label !== null && item.label !== '') {
    return String(item.label).trim();
  }

  return '';
};

/**
 * Custom square bar component
 * @param {any} properties - The properties
 * @returns {React.JSX.Element} The rendered triangle bar
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          className="bar-rect"
        />
      ))}
    </>
  );
}

/**
 * Power Consumption Report Chart
 * @param {PowerConsumptionReportChartProperties} properties - Component properties
 * @param {string} [properties.startDate] - Start date
 * @param {string} [properties.endDate] - End date
 * @returns {React.JSX.Element} The rendered component
 */
function PowerConsumptionReportChart({
  startDate = '',
  endDate = '',
}: PowerConsumptionReportChartProperties) {
  const { t, i18n } = useTranslation('sidePanel');
  const isRTL = i18n.dir() === 'rtl';
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState<ChartWidth>({
    type: 'percentage',
    width: 100,
  });

  // COLORS — read from theme CSS variables for light/dark support
  const labelColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-label-color')
    .trim();
  const barFillColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-bar-fill-color')
    .trim();

  /**
   * Function to show custom tooltip
   * @param {TooltipProps<number, string>} properties The tooltip props
   * @returns {React.JSX.Element | null} The rendered tooltip
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const isHourly =
        data?.time !== undefined ||
        data?.hour !== undefined ||
        (Boolean(startDate) && Boolean(endDate) && startDate === endDate);
      const displayDate =
        data?.date ||
        (Boolean(startDate) && Boolean(endDate) && startDate === endDate
          ? startDate
          : data?.label);

      return (
        <div className="energy-report-tooltip">
          <div className="tooltip-values">
            {isHourly ? (
              <>
                {displayDate && (
                  <p className="name-label">
                    {t('date')}: {displayDate}
                  </p>
                )}
                <p className="name-label">
                  {t('time')}: {data?.time || `${String(data?.hour ?? 0).padStart(2, '0')}:00`}
                </p>
              </>
            ) : (
              <p className="name-label">
                {t('date')}: {displayDate}
              </p>
            )}
            <p className="value-label">
              {t('totalPowerConsumed')}: {ToFixedValue(Number(data?.totalPowerConsumed), 3)} kW
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on date range
   * @returns {Promise<void>} Promise resolving when data is fetched
   */
  const fetchPowerConsumedReport = async () => {
    setIsLoading(true);
    try {
      const response = await stationManagement.fetchRevenueChart({
        startDate,
        endDate,
        type: 'powerConsumption',
        chargingStation: selectedChargingPoint?.id,
      });
      if (response.status === 200) {
        const rawList = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        // Normalize raw items: extract Mongo _id subfields (day, month, year) if present
        const normalizedList = rawList.map((item: any) => {
          const idObject = item._id && typeof item._id === 'object' ? item._id : {};
          const day = item.day ?? idObject.day;
          const month = item.month ?? item.monthName ?? idObject.month;
          const year = item.year ?? idObject.year;

          let derivedDate = item.date;
          if (!derivedDate && day && month) {
            const y =
              year || (startDate ? new Date(startDate).getFullYear() : new Date().getFullYear());
            const m = String(month).padStart(2, '0');
            const d = String(day).padStart(2, '0');
            derivedDate = `${y}-${m}-${d}`;
          }

          return {
            ...item,
            day,
            month,
            year,
            date: derivedDate || item.date || item.createdAt || '',
          };
        });

        // Sort items chronologically
        normalizedList.sort((a: any, b: any) => {
          const dateA = a.date ? new Date(a.date).getTime() : NaN;
          const dateB = b.date ? new Date(b.date).getTime() : NaN;
          if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
            return dateA - dateB;
          }
          if (a.year !== undefined && b.year !== undefined && a.year !== b.year) {
            return Number(a.year) - Number(b.year);
          }
          if (a.month !== undefined && b.month !== undefined && a.month !== b.month) {
            return Number(a.month) - Number(b.month);
          }
          if (a.day !== undefined && b.day !== undefined && a.day !== b.day) {
            return Number(a.day) - Number(b.day);
          }
          if (a.hour !== undefined && b.hour !== undefined && a.hour !== b.hour) {
            return Number(a.hour) - Number(b.hour);
          }
          return 0;
        });

        const formatted: ChartDataProperties[] = normalizedList.map((item: any) => ({
          ...item,
          label: formatChartLabel(item, t, i18n.language, startDate, endDate),
          date: item.date || (startDate && endDate && startDate === endDate ? startDate : ''),
          hour: item.hour,
          time: item.time,
          totalPowerConsumed: Number(
            item.totalPowerConsumed ??
              item.powerConsumedInWatts ??
              item.powerConsumed ??
              item.total ??
              item.value ??
              0,
          ),
        }));

        setBarGraphData(formatted);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport();
  }, [startDate, endDate, selectedChargingPoint?.id]);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);

  return (
    <div className="power-consumed-chart-wrap" dir={i18n.dir()}>
      <div className="power-consumed-container">
        <div className="power-consumed-header">
          <div className="power-consumed-title">
            <p>{t('powerConsumed')}</p>
          </div>
        </div>
        <div className="power-consumed-container">
          <div className="power-consumed-chart">
            {isLoading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={240}>
                <BarChart
                  data={barGraphData}
                  margin={{
                    top: 20,
                    right: isRTL ? 50 : 10,
                    left: isRTL ? 10 : 50,
                    bottom: 0,
                  }}>
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                    height={20}
                    reversed={isRTL}
                  />
                  <YAxis
                    orientation={isRTL ? 'right' : 'left'}
                    tickFormatter={(value) => `${ToFixedValue(value, 2)} kW`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: labelColor, fontWeight: 600 }}
                    width={isRTL ? 145 : 45}
                  />

                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="totalPowerConsumed"
                    name={t('powerConsumed')}
                    fill={barFillColor}
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

PowerConsumptionReportChart.defaultProps = {
  startDate: '',
  endDate: '',
};

export default PowerConsumptionReportChart;
